import type { Feature, FeatureCollection, Point, Polygon, MultiPolygon, Position } from "geojson";

export interface ParsedPolygonId {
	typePrefix: string;
	jobId: string;
	featureIndex: number;
	pathIndex: number;
}

/**
 * Parse a polygon feature ID like "boundary-abc123-0-0" into its components.
 * Format: {typePrefix}-{jobId}-{featureIndex}-{pathIndex}
 * jobId is alphanumeric (no hyphens), so we split on "-" and take last two as indices.
 */
export function parsePolygonId(id: string): ParsedPolygonId | null {
	if (!id || typeof id !== "string") return null;
	const parts = id.split("-");
	if (parts.length < 4) return null;

	const pathIndex = Number(parts[parts.length - 1]);
	const featureIndex = Number(parts[parts.length - 2]);
	if (Number.isNaN(pathIndex) || Number.isNaN(featureIndex)) return null;

	const typePrefix = parts[0];
	const jobId = parts.slice(1, -2).join("-");
	if (!jobId) return null;

	return { typePrefix, jobId, featureIndex, pathIndex };
}

const GEO_KEY_MAP: Record<string, "boundaryGeoJSON" | "zonesGeoJSON"> = {
	boundary: "boundaryGeoJSON",
	zones: "zonesGeoJSON",
};

export function geoJSONKeyForType(typePrefix: string): "boundaryGeoJSON" | "zonesGeoJSON" | null {
	return GEO_KEY_MAP[typePrefix] ?? null;
}

/**
 * Replace coordinates for a specific feature+path in a FeatureCollection.
 * - For Polygon: replaces geometry.coordinates with editedCoords
 * - For MultiPolygon: replaces geometry.coordinates[pathIndex] with editedCoords
 * Returns a new FeatureCollection (immutable).
 */
export function reconstructGeoJSON(
	original: FeatureCollection<Polygon | MultiPolygon>,
	featureIndex: number,
	pathIndex: number,
	editedCoords: Position[][],
): FeatureCollection<Polygon | MultiPolygon> {
	return {
		...original,
		features: original.features.map((feature, idx) => {
			if (idx !== featureIndex) return feature;

			const geom = feature.geometry;
			if (geom.type === "Polygon") {
				return {
					...feature,
					// Don't spread geom — it may carry a stale bbox that breaks turf
					geometry: { type: "Polygon" as const, coordinates: editedCoords },
				};
			}
			// MultiPolygon
			const newCoords = geom.coordinates.map((poly, pi) => (pi === pathIndex ? editedCoords : poly));
			return {
				...feature,
				// Don't spread geom — it may carry a stale bbox that breaks turf
				geometry: { type: "MultiPolygon" as const, coordinates: newCoords },
			};
		}),
	};
}

function isValidCoord(coord: Position): boolean {
	const [lng, lat] = coord;

	return (
		typeof lng === "number" &&
		typeof lat === "number" &&
		!Number.isNaN(lng) &&
		!Number.isNaN(lat) &&
		lng >= -180 &&
		lng <= 180 &&
		lat >= -90 &&
		lat <= 90
	);
}

function isFiniteCoord(coord: Position): boolean {
	return Number.isFinite(coord[0]) && Number.isFinite(coord[1]);
}

/** Max decimal places — coords are rounded to this before passing to terra-draw */
const COORD_PRECISION = 8;

function roundToPrecision(n: number): number {
	const factor = 10 ** COORD_PRECISION;
	return Math.round(n * factor) / factor;
}

function clampCoord(coord: Position): Position {
	const lng = roundToPrecision(Math.max(-180, Math.min(180, coord[0])));
	const lat = roundToPrecision(Math.max(-90, Math.min(90, coord[1])));
	return [lng, lat]; // strip altitude — terra-draw rejects 3D coords
}

/**
 * Ensure each ring is closed (first coord == last coord).
 * Terra-draw may strip the closing vertex from its snapshots,
 * producing invalid GeoJSON that breaks turf operations.
 */
export function ensureRingsClosed(coordinates: Position[][]): Position[][] {
	return coordinates.map((ring) => {
		if (ring.length < 2) return ring;
		const first = ring[0];
		const last = ring[ring.length - 1];
		if (first[0] !== last[0] || first[1] !== last[1]) {
			return [...ring, [...first]];
		}
		return ring;
	});
}

/**
 * Clean polygon coordinates so terra-draw accepts them:
 * - Remove NaN/Infinity coords
 * - Clamp out-of-range coords to valid lng/lat bounds
 * - Ensure rings are closed (first == last)
 */
export function cleanPolygonCoords(coordinates: Position[][]): Position[][] {
	return coordinates.map((ring) => {
		const cleaned = ring.filter(isFiniteCoord).map(clampCoord);
		// Ensure ring is closed
		if (cleaned.length >= 2) {
			const first = cleaned[0];
			const last = cleaned[cleaned.length - 1];
			if (first[0] !== last[0] || first[1] !== last[1]) {
				cleaned.push([...first]);
			}
		}
		return cleaned;
	});
}

/**
 * Find invalid coordinates in a polygon and return them as Point features
 * for rendering as red markers.
 */
export function findInvalidCoordinates(feature: Feature<Polygon>): Feature<Point>[] {
	const points: Feature<Point>[] = [];
	for (const ring of feature.geometry.coordinates) {
		for (const coord of ring) {
			if (!isValidCoord(coord)) {
				// Use clamped position for display (otherwise the point would be off-map)
				const displayCoord = clampCoord(coord);
				points.push({
					type: "Feature",
					properties: {
						invalid: true,
						originalLng: coord[0],
						originalLat: coord[1],
					},
					geometry: { type: "Point", coordinates: displayCoord },
				});
			}
		}
	}
	return points;
}
